import {
  type CopilotConfigurationTemplate,
  type ProjectCopilotConfigurationV1Snapshot,
} from '@factoryfixinc/ats-interfaces';

import { useAtsProjectsApi } from '@/composables/useApi';
import { InternalError } from '../shared/errors/internal.error';
import { HttpStatus } from '../shared/errors/enums/http-status.enum';

// Bounds the write request so a stalled save surfaces as a failure instead of locking the editor.
export const TEMPLATE_WRITE_TIMEOUT_MS = 30_000;

export class CopilotConfigurationTemplatePersistence {
  public async getTemplates(
    employerId: number,
    skip = 0,
    take = 100,
  ): Promise<CopilotConfigurationTemplate[]> {
    const url = `/copilot-configuration-template/employer/${employerId}?skip=${skip}&take=${take}`;

    const { data, error, statusCode } = await useAtsProjectsApi(url)
      .get()
      .json<CopilotConfigurationTemplate[]>();

    if (error?.value) {
      throw new InternalError('Could not get copilot configuration templates', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value ?? [];
  }

  /** Live jobs running on the default Copilot, i.e. with no custom recruiter selected. */
  public async getDefaultCopilotJobCount(employerId: number): Promise<number> {
    const url = `/copilot-configuration-template/employer/${employerId}/default-copilot-job-count`;

    const { data, error, statusCode } = await useAtsProjectsApi(url)
      .get()
      .json<{ projectCount: number }>();

    if (error?.value) {
      throw new InternalError('Could not get the default copilot job count', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value?.projectCount ?? 0;
  }

  public async getTemplateById(
    id: number,
    employerId: number,
  ): Promise<CopilotConfigurationTemplate | undefined> {
    const url = `/copilot-configuration-template/${id}/employer/${employerId}`;

    const { data, error, statusCode } = await useAtsProjectsApi(url)
      .get()
      .json<CopilotConfigurationTemplate>();

    if (statusCode.value === HttpStatus.NOT_FOUND) {
      return;
    }

    if (error?.value) {
      throw new InternalError('Could not get copilot configuration template', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value ?? undefined;
  }

  public async createTemplate(
    employerId: number,
    template: {
      displayName: string;
      description?: string;
      snapshot: ProjectCopilotConfigurationV1Snapshot;
    },
  ): Promise<CopilotConfigurationTemplate | undefined> {
    const url = `/copilot-configuration-template/employer/${employerId}`;

    const payload = {
      ...template,
      snapshot: JSON.stringify(template.snapshot),
    };

    const { data, error, statusCode } = await useAtsProjectsApi(url, {
      timeout: TEMPLATE_WRITE_TIMEOUT_MS,
    })
      .post(payload)
      .json<CopilotConfigurationTemplate>();

    if (error?.value) {
      throw new InternalError('Could not create copilot configuration template', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value ?? undefined;
  }

  public async updateTemplate(
    id: number,
    employerId: number,
    template: {
      displayName?: string;
      description?: string;
      snapshot?: ProjectCopilotConfigurationV1Snapshot;
    },
  ): Promise<CopilotConfigurationTemplate | undefined> {
    const url = `/copilot-configuration-template/${id}/employer/${employerId}`;

    const payload = {
      ...template,
      snapshot: template.snapshot ? JSON.stringify(template.snapshot) : undefined,
    };

    const { data, error, statusCode } = await useAtsProjectsApi(url, {
      timeout: TEMPLATE_WRITE_TIMEOUT_MS,
    })
      .patch(payload)
      .json<CopilotConfigurationTemplate>();

    if (statusCode.value === HttpStatus.NOT_FOUND) {
      return;
    }

    if (error?.value) {
      throw new InternalError('Could not update copilot configuration template', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value ?? undefined;
  }

  /** Hands the recruiter to another employer user, or makes it team-owned with `ownerId: null`. */
  public async transferTemplateOwnership(
    id: number,
    employerId: number,
    ownerId: number | null,
  ): Promise<CopilotConfigurationTemplate | undefined> {
    const url = `/copilot-configuration-template/${id}/employer/${employerId}/ownership`;

    const { data, error, statusCode } = await useAtsProjectsApi(url, {
      timeout: TEMPLATE_WRITE_TIMEOUT_MS,
    })
      .patch({ ownerId })
      .json<CopilotConfigurationTemplate>();

    if (statusCode.value === HttpStatus.NOT_FOUND) {
      return;
    }

    if (error?.value) {
      throw new InternalError('Could not transfer copilot configuration template ownership', {
        error: error.value,
        status: statusCode.value,
      });
    }

    return data.value ?? undefined;
  }

  public async deleteTemplate(id: number, employerId: number): Promise<void> {
    const url = `/copilot-configuration-template/${id}/employer/${employerId}`;

    const { error, statusCode } = await useAtsProjectsApi(url).delete();

    if (statusCode.value === HttpStatus.NOT_FOUND) {
      return;
    }

    if (error?.value) {
      throw new InternalError('Could not delete copilot configuration template', {
        error: error.value,
        status: statusCode.value,
      });
    }
  }
}
